import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './SettingsPage.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WhatsAppIntegration from './WhatsAppIntegration';
import { useTranslation } from 'react-i18next';
import {
  AtSign, Ban, BellRing, ChevronRight, CircleCheck, CreditCard, Database, Eye, EyeOff, Inbox, KeyRound,
  Lock, LogOut, MessagesSquare, Plug, Plus, RefreshCw, Send, ShieldCheck, Store, Trash2, TriangleAlert,
  UserRound, Warehouse, X,
} from 'lucide-react';
import { SiGmail, SiInstagram, SiMicrosoftoutlook, SiShopify, SiWhatsapp } from 'react-icons/si';
import {
  Badge, Banner, Card, ConfirmDialog, EmptyState, Field, IconChip, Kpi, Meter, Modal, OverflowMenu,
  Segmented, Switch, Tabs, Toast, initialsOf,
} from '../design/ui';
import { formatShortDate, formatShortDateTime, parseUtc } from '../utils/dates';
import { isEmployeeSession } from './accountScope';
import UserParameterEmployee from './UserParameterEmployee';

const TABS = {
  PROFILE: 'profile',
  INTEGRATIONS: 'integrations',
  NOTIFICATIONS: 'notifications',
  SECURITY: 'security'
};

const CHANNEL_TOTAL = 4; // Gmail, Outlook, WhatsApp, Instagram

// Graph dates carry 7 fractional digits, backend ones are naive UTC (KI-110):
// the shared parser handles both.
const parseDate = (value) => parseUtc(value);

const AccountParameters = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Core state — active tab lives in the URL so refresh and the OAuth
  // callback redirects land back on the right tab (?tab=integrations is also
  // the backend's link to the mailboxes: ticket journey, setup health).
  const tabParam = searchParams.get('tab');
  const activeTab = Object.values(TABS).includes(tabParam) ? tabParam : TABS.PROFILE;
  // The voice agent's numbers moved to their own section (Agents vocaux ›
  // Numéros, 2026-10-07): old links and Stripe returns land there.
  useEffect(() => {
    if (tabParam !== 'voice') return;
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    navigate(`/voice-numbers${next.toString() ? `?${next.toString()}` : ''}`, { replace: true });
  }, [tabParam, searchParams, navigate]);
  const setActiveTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === TABS.PROFILE) next.delete('tab'); else next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };
  const [user, setUser] = useState(undefined);
  const [isExperienceUser, setIsExperienceUser] = useState(false);

  // Modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  // One confirmation dialog for every destructive action: { title, message, confirmLabel, onConfirm }.
  const [confirmation, setConfirmation] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Delete confirmation
  const [confirmUsername, setConfirmUsername] = useState('');
  const [deleteError, setDeleteError] = useState('');

  // Instagram accounts selection
  const [, setInstagramAccounts] = useState([]);

  // Loading states
  const [isWmsConnecting, setIsWmsConnecting] = useState(false);
  const [isRegisteringWebhook, setIsRegisteringWebhook] = useState(false);
  const [isRemovingWebhook, setIsRemovingWebhook] = useState(false);
  const [isRegisteringSaveOnlyWebhook, setIsRegisteringSaveOnlyWebhook] = useState(false);

  // Outlook accounts
  const [outlookAccounts, setOutlookAccounts] = useState([]);
  const [, setIsLoadingOutlookAccounts] = useState(false);

  // Subscriptions
  const [subscriptions, setSubscriptions] = useState([]);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false);
  const [showSubscriptions, setShowSubscriptions] = useState(false);

  // Slack integration — the stored state comes from /user/slack-credentials
  // (masked token, channel); the inputs hold only what the person types.
  const [slackConfig, setSlackConfig] = useState(null);
  const [slackToken, setSlackToken] = useState('');
  const [slackChannelId, setSlackChannelId] = useState('');
  const [isLoadingSlackCredentials, setIsLoadingSlackCredentials] = useState(false);
  const [isSavingSlackCredentials, setIsSavingSlackCredentials] = useState(false);
  const [showSlackToken, setShowSlackToken] = useState(false);

  // Spacefill integration (ticketing platform, per-tenant API token)
  const [spacefillEnabled, setSpacefillEnabled] = useState(true);
  const [spacefillConfigured, setSpacefillConfigured] = useState(false);
  const [spacefillTokenSet, setSpacefillTokenSet] = useState(false);
  const [spacefillTokenInput, setSpacefillTokenInput] = useState('');
  const [spacefillSyncState, setSpacefillSyncState] = useState(null);
  const [spacefillOptions, setSpacefillOptions] = useState({});
  const [spacefillIncidentTypesInput, setSpacefillIncidentTypesInput] = useState('');
  const [isLoadingSpacefill, setIsLoadingSpacefill] = useState(false);
  const [isSavingSpacefill, setIsSavingSpacefill] = useState(false);
  const [showSpacefillToken, setShowSpacefillToken] = useState(false);
  const [spacefillFormOpen, setSpacefillFormOpen] = useState(false);

  // Ignored senders (addresses and domains that never open a ticket)
  const [blockedEntries, setBlockedEntries] = useState([]);
  const [blockedInvalid, setBlockedInvalid] = useState([]);
  const [newBlockedEntry, setNewBlockedEntry] = useState('');
  const [blockedEntryError, setBlockedEntryError] = useState('');
  const [isLoadingBlockedEmails, setIsLoadingBlockedEmails] = useState(false);
  const [isSavingBlockedEmails, setIsSavingBlockedEmails] = useState(false);
  const [blockedFilter, setBlockedFilter] = useState('all');

  // OTP change
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [newOtpCode, setNewOtpCode] = useState('');
  const [isSubmittingOtp, setIsSubmittingOtp] = useState(false);
  const [submitOtpError, setSubmitOtpError] = useState(null);
  const [submitOtpSuccess, setSubmitOtpSuccess] = useState(null);

  // The account's Gmail mailbox connected by app password (/api/logistics/smtp-config):
  // { mailbox, candidates (carrier pages' 2FA mailboxes), outlook_connected }.
  const [smtpConfig, setSmtpConfig] = useState(null);
  const [smtpReplacing, setSmtpReplacing] = useState(false);
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpForm, setSmtpForm] = useState({
    email: '',
    password: '',
    smtp_server: 'smtp.gmail.com',
    smtp_port: 587,
  });
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);

  // Feedback toast. setPopupMessage keeps its historical signature (a plain
  // translated string) — the tone is inferred from the message because the
  // call sites predate the toast.
  const [feedback, setFeedback] = useState(null);
  const feedbackTimerRef = useRef(null);
  const setPopupMessage = (message) => {
    if (!message) return;
    const isError = /fail|error|échec|impossible|erreur|invalid/i.test(message);
    clearTimeout(feedbackTimerRef.current);
    setFeedback({ type: isError ? 'error' : 'success', text: message });
    if (!isError) {
      feedbackTimerRef.current = setTimeout(() => setFeedback(null), 5000);
    }
  };
  useEffect(() => () => clearTimeout(feedbackTimerRef.current), []);

  // ============================================
  // INITIALIZATION
  // ============================================
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/');
      return;
    }

    const userDataString = localStorage.getItem('userData');
    if (userDataString) {
      try {
        const parsedUserData = JSON.parse(userDataString);
        if (parsedUserData.role === 'experience') {
          setIsExperienceUser(true);
          setUser({});
          return;
        }
      } catch (e) {
        console.error("Error parsing user data for experience check", e);
      }
    }

    fetchUserData(token);
    fetchOutlookAccounts();
    fetchSlackCredentials();
    fetchSpacefillConfig();
    fetchBlockedEmails();
    fetchSmtpConfig();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (user === null && !isExperienceUser) {
      navigate('/');
    }
  }, [user, navigate, isExperienceUser]);

  useEffect(() => {
    if (!isExperienceUser) {
      checkConnectionStatuses();
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      if (code && state) {
        handleInstagramCallback(code, state);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExperienceUser]);

  // ============================================
  // DATA FETCHING
  // ============================================
  const fetchUserData = async (token) => {
    try {
      const response = await axios.get('/api/users/user', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data) {
        setUser({
          username: response.data.username || '',
          firstname: response.data.firstname || '',
          lastname: response.data.lastname || '',
          email: response.data.email || '',
          role: response.data.role || '',
          subscription_plan: response.data.subscription_plan || '',
          subscription_start_date: response.data.subscription_start_date || null,
          subscription_end_date: response.data.subscription_end_date || null,
          billing_interval: response.data.billing_interval || '',
          is_subscribed: response.data.is_subscribed,
          user_meta_id: response.data.user_meta_id || '',
          user_insta_id: response.data.user_insta_id || '',
          gmail_email_address: response.data.gmail_email || '',
          automation: response.data.automation || '',
          shop_name: response.data.shop_name || '',
          instagram_username: response.data.insta_username || '',
          whatsapp_phone_number: response.data.whatsapp_phone_number || '',
          edn_api_account: response.data.edn_api_account || '',
          unanimity_wms_id: response.data.unanimity_wms_id || '',
          outlook_email: response.data.outlook_email || '',
          fa_account: response.data.fa_account || false,
        });
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      setUser(null);
    }
  };

  const fetchOutlookAccounts = async () => {
    if (isExperienceUser) return;
    try {
      setIsLoadingOutlookAccounts(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/social/outlook/accounts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOutlookAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Failed to fetch Outlook accounts:', error);
    } finally {
      setIsLoadingOutlookAccounts(false);
    }
  };

  const fetchSlackCredentials = async () => {
    if (isExperienceUser) return;
    try {
      setIsLoadingSlackCredentials(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/users/user/slack-credentials', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSlackConfig({
        maskedToken: response.data.slack_token || '',
        channelId: response.data.slack_channel_id || '',
        configured: !!response.data.is_configured,
      });
      setSlackToken('');
      setSlackChannelId(response.data.slack_channel_id || '');
    } catch (error) {
      console.error('Failed to fetch Slack credentials:', error?.response?.status);
    } finally {
      setIsLoadingSlackCredentials(false);
    }
  };

  const fetchBlockedEmails = async () => {
    if (isExperienceUser) return;
    try {
      setIsLoadingBlockedEmails(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/users/user/blocked-emails', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBlockedEntries(response.data.entries || []);
      setBlockedInvalid(response.data.invalid || []);
    } catch (error) {
      console.error('Failed to fetch blocked emails:', error);
    } finally {
      setIsLoadingBlockedEmails(false);
    }
  };

  // ============================================
  // CONNECTION STATUS CHECKS
  // ============================================
  const checkConnectionStatuses = () => {
    if (isExperienceUser) return;
    const urlParams = new URLSearchParams(window.location.search);
    const instaStatus = urlParams.get('status');
    const googleStatus = urlParams.get('google_status');
    const shopifyStatus = urlParams.get('shopify_status');
    const outlookStatus = urlParams.get('outlook_status');

    if (instaStatus === 'success') setPopupMessage(t('userParameter.instagramConnected', 'Compte Instagram connecté.'));
    else if (instaStatus === 'failure') setPopupMessage(t('userParameter.instagramConnectError', 'Échec de la connexion du compte Instagram.'));

    if (googleStatus === 'success') setPopupMessage(t('userParameter.gmailConnected', 'Gmail connecté.'));
    else if (googleStatus === 'failure') setPopupMessage(t('userParameter.gmailConnectError', 'Échec de la connexion du compte Google.'));

    if (shopifyStatus === 'success') setPopupMessage(t('userParameter.shopifyConnected', 'Boutique Shopify connectée.'));
    else if (shopifyStatus === 'failure') setPopupMessage(t('userParameter.shopifyConnectError', 'Échec de la connexion du compte Shopify.'));

    if (outlookStatus === 'success') setPopupMessage(t('userParameter.outlookConnected', 'Outlook connecté.'));
    else if (outlookStatus === 'failure') setPopupMessage(t('userParameter.outlookConnectError', 'Échec de la connexion du compte Outlook.'));
  };

  // ============================================
  // INSTAGRAM HANDLERS
  // ============================================
  const handleConnectInstagram = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/social/instagram/connect', {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Failed to connect Instagram account:', error);
    }
  };

  const handleInstagramCallback = async (code, state) => {
    if (isExperienceUser) return;
    const data = await fetchInstagramAccounts(code, state);
    if (data) {
      setInstagramAccounts(data.accounts);
    }
  };

  const fetchInstagramAccounts = async (code, state) => {
    if (isExperienceUser) return null;
    try {
      const response = await axios.get(`/api/social/instagram/callback?code=${code}&state=${state}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch Instagram accounts:', error);
      return null;
    }
  };

  const handleDisconnectInstagram = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete('/api/social/instagram/unsubscribe', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser({ ...user, user_insta_id: '', instagram_username: '' });
      setPopupMessage(t('settingsPage.instagramDisconnected', 'Compte Instagram déconnecté.'));
    } catch (error) {
      console.error('Failed to disconnect Instagram account:', error);
      setPopupMessage(t('settingsPage.instagramDisconnectError', 'Échec de la déconnexion du compte Instagram.'));
    }
  };

  // ============================================
  // GOOGLE HANDLERS
  // ============================================
  const handleConnectGoogle = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/social/google/connect', {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Failed to connect Google account:', error);
      setPopupMessage(t('userParameter.gmailConnectError', 'Échec de la connexion du compte Google.'));
    }
  };

  const handleDisconnectGoogle = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete('/api/social/google/unsubscribe', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser({ ...user, gmail_email_address: '' });
      setPopupMessage(t('settingsPage.gmailDisconnected', 'Gmail déconnecté.'));
    } catch (error) {
      console.error('Failed to disconnect Google account:', error);
      setPopupMessage(t('settingsPage.gmailDisconnectError', 'Échec de la déconnexion de Gmail.'));
    }
  };

  // ============================================
  // WHATSAPP HANDLERS
  // ============================================
  const handleDisconnectWhatsApp = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete('/api/social/disconnect/whatsappbusiness', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser({ ...user, whatsapp_phone_number: '' });
      setPopupMessage(t('settingsPage.whatsappDisconnected', 'WhatsApp déconnecté.'));
    } catch (error) {
      console.error('Failed to disconnect WhatsApp account:', error);
      setPopupMessage(t('settingsPage.whatsappDisconnectError', 'Échec de la déconnexion de WhatsApp.'));
    }
  };

  // ============================================
  // OUTLOOK HANDLERS
  // ============================================
  const handleConnectOutlook = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/social/outlook/connect', {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Failed to connect Outlook account:', error);
      setPopupMessage(t('userParameter.outlookConnectError', 'Échec de la connexion du compte Outlook.'));
    }
  };

  const handleDisconnectOutlook = async (outlookEmail) => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/social/outlook/unsubscribe/${outlookEmail}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPopupMessage(t('settingsPage.outlookRemoved', 'Compte Outlook {{email}} retiré.', { email: outlookEmail }));
      fetchOutlookAccounts();
      fetchUserData(token);
    } catch (error) {
      console.error('Failed to disconnect Outlook account:', error);
      setPopupMessage(t('settingsPage.outlookRemoveError', 'Échec du retrait du compte Outlook.'));
    }
  };

  const handleVerifySubscriptions = async () => {
    if (isExperienceUser) return;
    try {
      setIsLoadingSubscriptions(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/social/outlook/subscriptions', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscriptions(response.data.accounts || []);
      setShowSubscriptions(true);
    } catch (error) {
      console.error('Failed to fetch Outlook subscriptions:', error);
      setPopupMessage(t('settingsPage.subscriptionsError', 'Échec de la vérification des abonnements Outlook.'));
    } finally {
      setIsLoadingSubscriptions(false);
    }
  };

  // ============================================
  // WMS HANDLER
  // ============================================
  const handleConnectWMS = async () => {
    if (isExperienceUser) return;
    try {
      setIsWmsConnecting(true);
      const token = localStorage.getItem('access_token');
      await axios.post('/api/users/associate-wms', null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPopupMessage(t('settingsPage.wmsConnected', 'Unanimity WMS connecté.'));
      fetchUserData(token);
    } catch (error) {
      console.error('Failed to connect to WMS:', error);
      if (error.response?.status === 403) {
        setPopupMessage(t('settingsPage.wmsForbidden', 'Échec : seul le propriétaire du compte peut connecter le WMS.'));
      } else {
        setPopupMessage(t('settingsPage.wmsError', 'Échec de la connexion à Unanimity WMS.'));
      }
    } finally {
      setIsWmsConnecting(false);
    }
  };

  // ============================================
  // SHOPIFY WEBHOOK HANDLERS (legacy Shopify app)
  // ============================================
  const webhookError = (error) => error.response?.data?.message || error.message || 'Unknown error';

  const handleRegisterOrderWebhook = async () => {
    if (isExperienceUser) return;
    try {
      setIsRegisteringWebhook(true);
      const token = localStorage.getItem('access_token');
      await axios.post('/api/orders/shopify_live_webhooks/register_order_webhook', null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPopupMessage(t('settingsPage.webhookRegistered', 'Webhook de commandes Shopify enregistré.'));
    } catch (error) {
      console.error('Failed to register Shopify order webhook:', error);
      setPopupMessage(t('settingsPage.webhookRegisterError', 'Échec de l’enregistrement du webhook : {{error}}', { error: webhookError(error) }));
    } finally {
      setIsRegisteringWebhook(false);
    }
  };

  const handleRemoveOrderWebhook = async () => {
    if (isExperienceUser) return;
    try {
      setIsRemovingWebhook(true);
      const token = localStorage.getItem('access_token');
      await axios.delete('/api/orders/shopify_webhooks/remove_order_shopify_webhooks', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPopupMessage(t('settingsPage.webhooksRemoved', 'Webhooks de commandes Shopify retirés.'));
    } catch (error) {
      console.error('Failed to remove Shopify order webhooks:', error);
      setPopupMessage(t('settingsPage.webhooksRemoveError', 'Échec du retrait des webhooks : {{error}}', { error: webhookError(error) }));
    } finally {
      setIsRemovingWebhook(false);
    }
  };

  const handleRegisterSaveOnlyWebhook = async () => {
    if (isExperienceUser) return;
    try {
      setIsRegisteringSaveOnlyWebhook(true);
      const token = localStorage.getItem('access_token');
      await axios.post('/api/orders/shopify_webhooks/register_order_shopify_webhooks', null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPopupMessage(t('settingsPage.saveOnlyRegistered', 'Webhook « enregistrement seul » Shopify enregistré.'));
    } catch (error) {
      console.error('Failed to register Shopify save-only order webhook:', error);
      setPopupMessage(t('settingsPage.webhookRegisterError', 'Échec de l’enregistrement du webhook : {{error}}', { error: webhookError(error) }));
    } finally {
      setIsRegisteringSaveOnlyWebhook(false);
    }
  };

  // ============================================
  // PASSWORD HANDLER
  // ============================================
  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordError('');
  };

  const handlePasswordSubmit = async (e) => {
    if (isExperienceUser) return;
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      setPasswordError(t('settingsPage.passwordMismatch', 'Les deux nouveaux mots de passe ne correspondent pas.'));
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      await axios.put('/api/users/update-user', {
        currentPassword,
        password: newPassword,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      closePasswordModal();
      setPopupMessage(t('settingsPage.passwordUpdated', 'Mot de passe mis à jour.'));
    } catch (error) {
      console.error('Failed to update password:', error?.response?.status);
      setPasswordError(t('settingsPage.passwordError', 'Le mot de passe n’a pas pu être changé. Vérifiez le mot de passe actuel.'));
    }
  };

  // ============================================
  // DELETE ACCOUNT HANDLER
  // ============================================
  const handleDeleteUser = async (e) => {
    if (isExperienceUser) return;
    e.preventDefault();
    if (confirmUsername !== user.username) {
      setDeleteError(t('settingsPage.deleteMismatch', 'Le nom saisi ne correspond pas au nom d’utilisateur.'));
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete('/api/users/user/delete', {
        headers: { Authorization: `Bearer ${token}` },
      });
      localStorage.removeItem('access_token');
      navigate('/');
    } catch (error) {
      console.error('Failed to delete user account:', error);
      setDeleteError(t('userParameter.deleteAccountError', 'Échec de la suppression du compte.'));
    }
  };

  // ============================================
  // SLACK CREDENTIALS HANDLER
  // ============================================
  // Only what changed is sent: the backend ignores absent fields, so a stored
  // token is kept unless a new one is typed.
  const slackPayload = () => {
    const payload = {};
    if (slackToken.trim()) payload.slack_token = slackToken.trim();
    if (slackChannelId.trim() && slackChannelId.trim() !== (slackConfig?.channelId || '')) {
      payload.slack_channel_id = slackChannelId.trim();
    }
    return payload;
  };

  const handleSaveSlackCredentials = async () => {
    if (isExperienceUser) return;
    const payload = slackPayload();
    if (!Object.keys(payload).length) return;
    try {
      setIsSavingSlackCredentials(true);
      const token = localStorage.getItem('access_token');
      await axios.put('/api/users/user/slack-credentials', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPopupMessage(t('settingsPage.slackSaved', 'Alertes Slack enregistrées.'));
      fetchSlackCredentials();
    } catch (error) {
      console.error('Failed to update Slack credentials:', error?.response?.status);
      setPopupMessage(t('settingsPage.slackError', 'Échec de l’enregistrement des alertes Slack.'));
    } finally {
      setIsSavingSlackCredentials(false);
    }
  };

  // ============================================
  // SPACEFILL INTEGRATION HANDLERS
  // ============================================
  const fetchSpacefillConfig = async () => {
    if (isExperienceUser) return;
    try {
      setIsLoadingSpacefill(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/integrations/config', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const config = (response.data?.configs || []).find(
        (c) => c.integration_key === 'spacefill'
      );
      if (config) {
        setSpacefillConfigured(true);
        setSpacefillEnabled(config.enabled !== false);
        setSpacefillTokenSet(!!config.auth?.token);
        setSpacefillSyncState(config.sync_state || null);
        const options = config.options || {};
        setSpacefillOptions(options);
        setSpacefillIncidentTypesInput(
          Array.isArray(options.incident_types) ? options.incident_types.join(', ') : ''
        );
      }
    } catch (error) {
      console.error('Failed to fetch Spacefill configuration:', error);
    } finally {
      setIsLoadingSpacefill(false);
    }
  };

  const handleSaveSpacefillConfig = async () => {
    if (isExperienceUser) return;
    const newToken = spacefillTokenInput.trim();
    if (!spacefillTokenSet && !newToken) {
      setPopupMessage(t('settingsPage.spacefillTokenRequired', 'Échec : collez le jeton d’API Spacefill.'));
      return;
    }
    try {
      setIsSavingSpacefill(true);
      const token = localStorage.getItem('access_token');
      const payload = { enabled: spacefillEnabled };
      // Only send auth when a new token was typed; the backend keeps the
      // stored token otherwise.
      if (newToken) {
        payload.auth = { type: 'bearer', token: newToken };
      }
      // options replaces the stored object wholesale: merge over the fetched
      // options so other knobs (initial_lookback_days...) are preserved.
      const incidentTypes = spacefillIncidentTypesInput
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const options = { ...spacefillOptions };
      if (incidentTypes.length > 0) {
        options.incident_types = incidentTypes;
      } else {
        delete options.incident_types;
      }
      payload.options = options;
      await axios.put('/api/integrations/config/spacefill', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSpacefillConfigured(true);
      setSpacefillOptions(options);
      if (newToken) {
        setSpacefillTokenSet(true);
        setSpacefillTokenInput('');
      }
      setPopupMessage(t('userParameter.spacefillSaved', 'Spacefill configuration saved! Incidents will sync within 5 minutes.'));
    } catch (error) {
      console.error('Failed to save Spacefill configuration:', error);
      setPopupMessage(t('userParameter.spacefillError', 'Failed to save Spacefill configuration.'));
    } finally {
      setIsSavingSpacefill(false);
    }
  };

  // ============================================
  // BLOCKED EMAILS HANDLER
  // ============================================
  const BLOCKED_EMAIL_RE = /^[a-z0-9._%+'-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
  const BLOCKED_DOMAIN_RE = /^(?!-)[a-z0-9-]+(\.[a-z0-9-]+)+$/i;
  const classifyBlockedEntry = (raw) => {
    const value = String(raw || '').trim().toLowerCase().replace(/^@/, '');
    if (!value) return null;
    if (BLOCKED_EMAIL_RE.test(value)) return { value, kind: 'email' };
    if (BLOCKED_DOMAIN_RE.test(value)) return { value, kind: 'domain' };
    return null;
  };

  const saveBlockedEntries = async (entries) => {
    if (isExperienceUser) return;
    try {
      setIsSavingBlockedEmails(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.put('/api/users/user/blocked-emails', {
        entries: entries.map((entry) => entry.value),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlockedEntries(response.data.entries || entries);
      setBlockedInvalid(response.data.invalid || []);
      setPopupMessage(t('userParameter.blockedEmailsSaved', 'Ignored senders updated.'));
    } catch (error) {
      console.error('Failed to update ignored senders:', error);
      const detail = error.response?.data?.detail;
      const invalid = detail && typeof detail === 'object' && Array.isArray(detail.invalid) ? detail.invalid.join(', ') : '';
      setPopupMessage(
        invalid
          ? t('userParameter.blockedSendersInvalid', 'Not an e-mail address or a domain: {{entries}}', { entries: invalid })
          : t('userParameter.blockedEmailsError', 'Failed to update ignored senders.')
      );
    } finally {
      setIsSavingBlockedEmails(false);
    }
  };

  const handleAddBlockedEntry = async () => {
    const entry = classifyBlockedEntry(newBlockedEntry);
    if (!entry) {
      setBlockedEntryError(t('userParameter.blockedSendersInvalidOne', 'Enter an e-mail address (name@example.com) or a domain (example.com).'));
      return;
    }
    setBlockedEntryError('');
    if (blockedEntries.some((existing) => existing.value === entry.value)) {
      setNewBlockedEntry('');
      return;
    }
    const next = [...blockedEntries, entry];
    setNewBlockedEntry('');
    await saveBlockedEntries(next);
  };

  const handleRemoveBlockedEntry = async (value) => {
    await saveBlockedEntries(blockedEntries.filter((entry) => entry.value !== value));
  };

  // ============================================
  // SMTP CONFIG HANDLERS
  // ============================================
  const smtpErrorText = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    return typeof detail === 'string' && detail ? detail : fallback;
  };

  const fetchSmtpConfig = async () => {
    if (isExperienceUser) return;
    try {
      setSmtpLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/logistics/smtp-config', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSmtpConfig(response.data || null);
      const mailbox = response.data?.mailbox;
      setSmtpForm({
        email: mailbox?.email || '',
        password: '',
        smtp_server: mailbox?.smtp_server || 'smtp.gmail.com',
        smtp_port: mailbox?.smtp_port || 587,
      });
    } catch {
      setSmtpConfig(null);
    } finally {
      setSmtpLoading(false);
    }
  };

  const handleSaveSmtpConfig = async () => {
    if (isExperienceUser) return;
    try {
      setSmtpSaving(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.put('/api/logistics/smtp-config', {
        email: smtpForm.email,
        password: smtpForm.password,
        smtp_server: smtpForm.smtp_server,
        smtp_port: Number(smtpForm.smtp_port),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSmtpConfig(response.data);
      setSmtpReplacing(false);
      setSmtpForm((f) => ({ ...f, password: '' }));
      setPopupMessage(t('accountMailbox.connected', 'Boîte Gmail du compte connectée.'));
    } catch (error) {
      console.error('Failed to connect the account mailbox:', error?.response?.status);
      setPopupMessage(smtpErrorText(error, t('accountMailbox.connectError', 'Échec de la connexion de la boîte.')));
    } finally {
      setSmtpSaving(false);
    }
  };

  const handleUseOtpMailbox = async (carrierId) => {
    if (isExperienceUser) return;
    try {
      setSmtpSaving(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.post('/api/logistics/smtp-config/from-otp-mailbox', { carrier_id: carrierId }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSmtpConfig(response.data);
      setSmtpReplacing(false);
      setPopupMessage(t('accountMailbox.connected', 'Boîte Gmail du compte connectée.'));
    } catch (error) {
      console.error('Failed to connect the account mailbox from the 2FA mailbox:', error?.response?.status);
      setPopupMessage(smtpErrorText(error, t('accountMailbox.connectError', 'Échec de la connexion de la boîte.')));
    } finally {
      setSmtpSaving(false);
    }
  };

  const handleRemoveSmtpConfig = async () => {
    if (isExperienceUser) return;
    try {
      setSmtpSaving(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.delete('/api/logistics/smtp-config', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSmtpConfig(response.data);
      setSmtpForm({ email: '', password: '', smtp_server: 'smtp.gmail.com', smtp_port: 587 });
      setPopupMessage(t('accountMailbox.disconnected', 'Boîte Gmail du compte déconnectée.'));
    } catch (error) {
      console.error('Failed to disconnect the account mailbox:', error?.response?.status);
      setPopupMessage(smtpErrorText(error, t('accountMailbox.disconnectError', 'Échec de la déconnexion de la boîte.')));
    } finally {
      setSmtpSaving(false);
    }
  };

  // ============================================
  // OTP CHANGE HANDLERS
  // ============================================
  const handleOpenOtpModal = () => {
    setShowOtpModal(true);
    setSubmitOtpError(null);
    setSubmitOtpSuccess(null);
    setNewOtpCode('');
  };

  const handleCloseOtpModal = () => {
    setShowOtpModal(false);
    setNewOtpCode('');
    setSubmitOtpError(null);
    setSubmitOtpSuccess(null);
  };

  const handleSubmitOtpChange = async (e) => {
    e.preventDefault();
    setIsSubmittingOtp(true);
    setSubmitOtpError(null);
    setSubmitOtpSuccess(null);

    const token = localStorage.getItem('access_token');

    if (!newOtpCode.trim()) {
      setSubmitOtpError(t('otpCodeRequired', 'OTP code is required.'));
      setIsSubmittingOtp(false);
      return;
    }

    try {
      await axios.put('/api/users/user/otp',
        { otp_code: newOtpCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSubmitOtpSuccess(t('otpUpdatedSuccessfully', 'OTP code updated successfully!'));
      setNewOtpCode('');
      setTimeout(() => {
        handleCloseOtpModal();
      }, 2500);
    } catch (error) {
      console.error('Failed to update OTP code:', error);
      setSubmitOtpError(
        error.response?.data?.detail ||
        t('failedToUpdateOtp', 'Failed to update OTP code. Please try again.')
      );
    } finally {
      setIsSubmittingOtp(false);
    }
  };

  // ============================================
  // CONFIRMATION (every disconnect goes through it)
  // ============================================
  const askConfirmation = (request) => setConfirmation(request);
  const runConfirmation = async () => {
    if (!confirmation) return;
    setIsConfirming(true);
    try {
      await confirmation.onConfirm();
    } finally {
      setIsConfirming(false);
      setConfirmation(null);
    }
  };

  // ============================================
  // FORMATTING
  // ============================================
  // The shared short format (utils/dates): « 28 oct. », « 28 oct., 14:05 ».
  const lang = i18n.language || 'fr';
  const formatDate = (value) => formatShortDate(value, lang, { empty: '' });
  const formatDateTime = (value) => formatShortDateTime(value, lang, { empty: '' });
  const daysUntil = (value) => {
    const date = parseDate(value);
    return date ? Math.ceil((date.getTime() - Date.now()) / 86400000) : null;
  };

  // ============================================
  // EXPERIENCE USER VIEW
  // ============================================
  if (isExperienceUser) {
    return (
      <div className="u-page u-page--narrow settings-v2">
        <div className="u-card">
          <EmptyState
            icon={Ban}
            title={t('userParameter.experience.title', 'Settings Not Available')}
            action={(
              <button type="button" onClick={() => navigate('/dashboard')} className="u-btn u-btn--secondary">
                {t('userParameter.experience.backToDashboard', 'Back to Dashboard')}
              </button>
            )}
          >
            {t('userParameter.experience.message', 'The user parameters page is currently not available for experience accounts.')}
          </EmptyState>
        </div>
      </div>
    );
  }

  // ============================================
  // LOADING STATE
  // ============================================
  if (user === undefined) {
    return (
      <div className="u-page u-page--narrow settings-v2" aria-busy="true" aria-label={t('userParameter.loading', 'Loading settings...')}>
        <div className="u-card settings-v2__hero">
          <div className="settings-v2__identity">
            <span className="u-skeleton" style={{ width: 64, height: 64, borderRadius: 18 }} />
            <div style={{ flex: 1 }}>
              <span className="u-skeleton" style={{ width: 120, height: 12, marginBottom: 10 }} />
              <span className="u-skeleton" style={{ width: 260, height: 24 }} />
            </div>
          </div>
        </div>
        <div className="u-kpi-row">
          {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton" style={{ height: 104, borderRadius: 16 }} />)}
        </div>
      </div>
    );
  }

  // ============================================
  // ERROR STATE
  // ============================================
  if (!user) {
    return (
      <div className="u-page u-page--narrow settings-v2">
        <div className="u-card">
          <EmptyState icon={TriangleAlert} title={t('userParameter.loadError', 'Failed to load user data')} />
        </div>
      </div>
    );
  }

  // ============================================
  // DERIVED STATE — what the page tells at a glance
  // ============================================
  const displayName = user.shop_name
    || [user.firstname, user.lastname].filter(Boolean).join(' ')
    || user.username
    || user.email;
  const roleLabels = {
    admin: t('sidebarNav.roleAdmin', 'Administrateur'),
    logistics: t('sidebarNav.roleLogistics', 'Logistique'),
  };

  const mailbox = smtpConfig?.mailbox || null;
  const candidates = smtpConfig?.candidates || [];

  const channels = [
    {
      key: 'gmail',
      name: 'Gmail',
      logo: <SiGmail color="#EA4335" />,
      connected: !!user.gmail_email_address,
      detail: user.gmail_email_address,
      hint: t('settingsPage.gmailHint', 'Les e-mails reçus dans votre boîte Gmail deviennent des tickets.'),
      onConnect: handleConnectGoogle,
      onDisconnect: () => askConfirmation({
        title: t('settingsPage.confirmGmailTitle', 'Déconnecter Gmail ?'),
        message: t('settingsPage.confirmChannelMessage', 'Les nouveaux messages reçus sur {{channel}} n’arriveront plus dans Unanimity. Les tickets existants sont conservés.', { channel: user.gmail_email_address || 'Gmail' }),
        confirmLabel: t('settingsPage.disconnect', 'Déconnecter'),
        onConfirm: handleDisconnectGoogle,
      }),
    },
    {
      key: 'outlook',
      name: 'Outlook',
      logo: <SiMicrosoftoutlook color="#0A64C8" />,
      connected: outlookAccounts.length > 0,
      detail: outlookAccounts.length > 0
        ? t('settingsPage.outlookCount', '{{count}} compte connecté', { count: outlookAccounts.length })
        : null,
      hint: t('settingsPage.outlookHint', 'Chaque compte Outlook connecté reçoit ses e-mails dans Unanimity.'),
      onConnect: handleConnectOutlook,
      multi: true,
    },
    {
      key: 'whatsapp',
      name: 'WhatsApp',
      logo: <SiWhatsapp color="#25D366" />,
      connected: !!user.whatsapp_phone_number,
      detail: user.whatsapp_phone_number,
      hint: t('settingsPage.whatsappHint', 'Les messages WhatsApp Business deviennent des tickets.'),
      onConnect: () => setShowWhatsAppModal(true),
      onDisconnect: () => askConfirmation({
        title: t('settingsPage.confirmWhatsappTitle', 'Déconnecter WhatsApp ?'),
        message: t('settingsPage.confirmChannelMessage', 'Les nouveaux messages reçus sur {{channel}} n’arriveront plus dans Unanimity. Les tickets existants sont conservés.', { channel: user.whatsapp_phone_number || 'WhatsApp' }),
        confirmLabel: t('settingsPage.disconnect', 'Déconnecter'),
        onConfirm: handleDisconnectWhatsApp,
      }),
    },
    {
      key: 'instagram',
      name: 'Instagram',
      logo: <SiInstagram color="#E1306C" />,
      connected: !!user.user_insta_id,
      detail: user.instagram_username ? `@${user.instagram_username}` : null,
      hint: t('settingsPage.instagramHint', 'Les messages privés Instagram deviennent des tickets.'),
      onConnect: handleConnectInstagram,
      onDisconnect: () => askConfirmation({
        title: t('settingsPage.confirmInstagramTitle', 'Déconnecter Instagram ?'),
        message: t('settingsPage.confirmChannelMessage', 'Les nouveaux messages reçus sur {{channel}} n’arriveront plus dans Unanimity. Les tickets existants sont conservés.', { channel: user.instagram_username ? `@${user.instagram_username}` : 'Instagram' }),
        confirmLabel: t('settingsPage.disconnect', 'Déconnecter'),
        onConfirm: handleDisconnectInstagram,
      }),
    },
  ];
  const connectedChannels = channels.filter((channel) => channel.connected);

  const expiredOutlook = outlookAccounts.filter((account) => {
    const days = daysUntil(account.subscription_expiration);
    return days !== null && days < 0;
  });

  const blockedCounts = {
    email: blockedEntries.filter((entry) => entry.kind === 'email').length,
    domain: blockedEntries.filter((entry) => entry.kind === 'domain').length,
  };
  const visibleBlocked = blockedFilter === 'all'
    ? blockedEntries
    : blockedEntries.filter((entry) => entry.kind === blockedFilter);

  const planEnd = parseDate(user.subscription_end_date);
  const planStart = parseDate(user.subscription_start_date);
  const planDaysLeft = daysUntil(user.subscription_end_date);
  const planProgress = planStart && planEnd && planEnd > planStart
    ? Math.round(((Date.now() - planStart.getTime()) / (planEnd.getTime() - planStart.getTime())) * 100)
    : null;
  const intervalLabels = {
    month: t('settingsPage.monthly', 'Mensuel'),
    monthly: t('settingsPage.monthly', 'Mensuel'),
    year: t('settingsPage.yearly', 'Annuel'),
    yearly: t('settingsPage.yearly', 'Annuel'),
    annual: t('settingsPage.yearly', 'Annuel'),
  };

  // Things that need a person, newest risk first. Never a guess: each item is
  // a field the backend returned.
  const attention = [
    mailbox?.problem && {
      key: 'mailbox',
      tone: 'danger',
      title: t('settingsPage.attentionMailboxTitle', 'La boîte Gmail du compte ne fonctionne plus'),
      detail: mailbox.problem,
      tab: TABS.INTEGRATIONS,
    },
    ...expiredOutlook.map((account) => ({
      key: `outlook-${account.outlook_email}`,
      tone: 'warning',
      title: t('settingsPage.attentionOutlookTitle', 'Abonnement Outlook expiré pour {{email}}', { email: account.outlook_email }),
      detail: t('settingsPage.attentionOutlookDetail', 'Expiré le {{date}} : ses nouveaux e-mails n’arrivent peut-être plus. Vérifiez les abonnements.', { date: formatDate(account.subscription_expiration) }),
      tab: TABS.INTEGRATIONS,
    })),
    spacefillConfigured && spacefillEnabled && spacefillSyncState?.last_error && {
      key: 'spacefill',
      tone: 'warning',
      title: t('settingsPage.attentionSpacefillTitle', 'La synchronisation Spacefill échoue'),
      detail: spacefillSyncState.last_error,
      tab: TABS.INTEGRATIONS,
    },
    blockedInvalid.length > 0 && {
      key: 'blocked',
      tone: 'warning',
      title: t('settingsPage.attentionBlockedTitle', '{{count}} expéditeur ignoré est illisible', { count: blockedInvalid.length }),
      detail: blockedInvalid.join(', '),
      tab: TABS.NOTIFICATIONS,
    },
  ].filter(Boolean);

  const tabs = [
    { key: TABS.PROFILE, label: t('settingsPage.tabAccount', 'Compte'), icon: UserRound },
    { key: TABS.INTEGRATIONS, label: t('settingsPage.tabChannels', 'Canaux et boîtes mail'), icon: Inbox },
    { key: TABS.NOTIFICATIONS, label: t('settingsPage.tabAlerts', 'Alertes et filtres'), icon: BellRing },
    { key: TABS.SECURITY, label: t('settingsPage.tabSecurity', 'Sécurité'), icon: ShieldCheck },
  ];

  const connectedBadge = (
    <Badge tone="success" dot>{t('settingsPage.connected', 'Connecté')}</Badge>
  );
  const notConnectedBadge = <Badge>{t('settingsPage.notConnected', 'Non connecté')}</Badge>;

  // ============================================
  // PANELS
  // ============================================
  const renderProfile = () => {
    const facts = [
      { label: t('settingsPage.username', 'Nom d’utilisateur'), value: user.username },
      { label: t('settingsPage.email', 'E-mail'), value: user.email },
      { label: t('settingsPage.firstname', 'Prénom'), value: user.firstname },
      { label: t('settingsPage.lastname', 'Nom'), value: user.lastname },
      { label: t('settingsPage.shopName', 'Boutique'), value: user.shop_name },
      { label: t('settingsPage.role', 'Rôle'), value: roleLabels[user.role] || user.role },
    ].filter((fact) => fact.value);
    return (
      <div className="u-grid-2 settings-v2__profile">
        <Card
          icon={UserRound}
          title={t('settingsPage.identityTitle', 'Identité')}
          description={t('settingsPage.identityDesc', 'Les informations de connexion du compte.')}
        >
          <dl className="settings-v2__facts">
            {facts.map((fact) => (
              <div key={fact.label}>
                <dt className="u-overline">{fact.label}</dt>
                <dd>{fact.value}</dd>
              </div>
            ))}
          </dl>
        </Card>

        {user.subscription_plan ? (
          <Card
            icon={CreditCard}
            title={t('settingsPage.planTitle', 'Abonnement')}
            badge={user.is_subscribed === false
              ? <Badge tone="warning" dot>{t('settingsPage.planInactive', 'Inactif')}</Badge>
              : user.is_subscribed === true && <Badge tone="success" dot>{t('settingsPage.planActive', 'Actif')}</Badge>}
            description={t('settingsPage.planDesc', 'Votre formule et sa période en cours.')}
            footer={(
              <button type="button" className="u-btn u-btn--secondary" onClick={() => navigate('/billing')}>
                {t('settingsPage.manageBilling', 'Gérer la facturation')}
                <ChevronRight aria-hidden="true" />
              </button>
            )}
          >
            <div className="settings-v2__plan">
              <div className="settings-v2__plan-name">{user.subscription_plan}</div>
              {intervalLabels[String(user.billing_interval).toLowerCase()] && (
                <Badge tone="accent">{intervalLabels[String(user.billing_interval).toLowerCase()]}</Badge>
              )}
            </div>
            {planProgress !== null && (
              <div className="settings-v2__period">
                <div className="settings-v2__period-dates">
                  <span>{formatDate(user.subscription_start_date)}</span>
                  <span>{formatDate(user.subscription_end_date)}</span>
                </div>
                <Meter value={Math.max(0, Math.min(100, planProgress))} max={100} label={t('settingsPage.planPeriod', 'Période en cours')} />
                {planDaysLeft !== null && planDaysLeft >= 0 && (
                  <p className="u-help">
                    {t('settingsPage.planDaysLeft', 'Renouvellement dans {{count}} jour', { count: planDaysLeft })}
                  </p>
                )}
              </div>
            )}
          </Card>
        ) : null}
      </div>
    );
  };

  const renderChannelRow = (channel) => (
    <li className="u-row" key={channel.key}>
      <span className="u-logo" aria-hidden="true">{channel.logo}</span>
      <div className="u-row__main">
        <div className="u-row__title">
          {channel.name}
          {channel.connected ? connectedBadge : notConnectedBadge}
        </div>
        <div className="u-row__meta" title={channel.detail || channel.hint}>
          {channel.connected && channel.detail ? channel.detail : channel.hint}
        </div>
      </div>
      <div className="u-row__side">
        {channel.multi ? (
          <>
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={channel.onConnect}>
              <Plus aria-hidden="true" />
              {channel.connected ? t('settingsPage.addAccount', 'Ajouter un compte') : t('settingsPage.connect', 'Connecter')}
            </button>
            {channel.connected && (
              <OverflowMenu
                label={t('settingsPage.moreActions', 'Plus d’actions')}
                items={[{
                  label: isLoadingSubscriptions
                    ? t('settingsPage.verifying', 'Vérification…')
                    : t('settingsPage.verifySubscriptions', 'Vérifier les abonnements'),
                  icon: RefreshCw,
                  onClick: handleVerifySubscriptions,
                  disabled: isLoadingSubscriptions,
                }]}
              />
            )}
          </>
        ) : channel.connected ? (
          <OverflowMenu
            label={t('settingsPage.moreActions', 'Plus d’actions')}
            items={[{ label: t('settingsPage.disconnect', 'Déconnecter'), icon: LogOut, onClick: channel.onDisconnect, danger: true }]}
          />
        ) : (
          <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={channel.onConnect}>
            {t('settingsPage.connect', 'Connecter')}
          </button>
        )}
      </div>
    </li>
  );

  const renderOutlookAccounts = () => outlookAccounts.length > 0 && (
    <ul className="u-list settings-v2__sublist">
      {outlookAccounts.map((account) => {
        const days = daysUntil(account.subscription_expiration);
        return (
          <li className="u-row" key={account.outlook_email}>
            <IconChip icon={AtSign} tone="neutral" size="sm" />
            <div className="u-row__main">
              <div className="u-row__title">
                {account.outlook_email}
                {days !== null && days < 0 && <Badge tone="warning" dot>{t('settingsPage.expired', 'Abonnement expiré')}</Badge>}
              </div>
              <div className="u-row__meta">
                {account.added_at && t('settingsPage.addedOn', 'Ajouté le {{date}}', { date: formatDate(account.added_at) })}
                {account.subscription_expiration && days !== null && days >= 0 && (
                  <> · {t('settingsPage.renewsOn', 'abonnement jusqu’au {{date}}', { date: formatDate(account.subscription_expiration) })}</>
                )}
              </div>
            </div>
            <div className="u-row__side">
              <OverflowMenu
                label={t('settingsPage.moreActions', 'Plus d’actions')}
                items={[{
                  label: t('settingsPage.removeAccount', 'Retirer ce compte'),
                  icon: Trash2,
                  danger: true,
                  onClick: () => askConfirmation({
                    title: t('settingsPage.confirmOutlookTitle', 'Retirer {{email}} ?', { email: account.outlook_email }),
                    message: t('settingsPage.confirmOutlookMessage', 'Les nouveaux e-mails de ce compte n’arriveront plus dans Unanimity. Les tickets existants sont conservés.'),
                    confirmLabel: t('settingsPage.removeAccount', 'Retirer ce compte'),
                    onConfirm: () => handleDisconnectOutlook(account.outlook_email),
                  }),
                }]}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );

  const renderSubscriptions = () => showSubscriptions && subscriptions.length > 0 && (
    <div className="settings-v2__subscriptions">
      <div className="settings-v2__subscriptions-head">
        <span className="u-overline">{t('settingsPage.subscriptionStatus', 'État des abonnements Outlook')}</span>
        <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm" onClick={() => setShowSubscriptions(false)} aria-label={t('common.close', 'Fermer')} title={t('common.close', 'Fermer')}>
          <X aria-hidden="true" />
        </button>
      </div>
      {subscriptions.map((account) => (
        <div key={account.email} className="settings-v2__subscription">
          <span className="settings-v2__subscription-email">{account.email}</span>
          <span className="settings-v2__subscription-badges">
            {account.error ? (
              <Badge tone="danger" icon={TriangleAlert}>{account.error}</Badge>
            ) : (
              <>
                <Badge tone="success">{t('settingsPage.subscriptionCount', '{{count}} abonnement', { count: account.subscriptions?.length || 0 })}</Badge>
                {account.subscriptions?.map((sub) => (
                  <Badge key={`${account.email}-${sub.resource}`} tone={sub.matches_this_account ? 'success' : 'warning'} icon={sub.matches_this_account ? CircleCheck : TriangleAlert}>
                    {sub.resource}
                  </Badge>
                ))}
              </>
            )}
          </span>
        </div>
      ))}
    </div>
  );

  const renderCarrierMailbox = () => {
    // Shown while no Outlook is connected (unchanged rule), and always when a
    // mailbox exists so a connected box can never disappear from the page.
    if (outlookAccounts.length > 0 && !mailbox) return null;
    const originPage = mailbox?.origin?.kind === 'carrier_otp_mailbox'
      ? candidates.find((c) => c.carrier_id === mailbox.origin.carrier_id) : null;
    const showForm = !mailbox || smtpReplacing;
    const otherCandidates = candidates.filter((c) => c.email !== mailbox?.email);
    return (
      <Card
        icon={Send}
        title={t('settingsPage.carrierMailTitle', 'E-mails aux transporteurs')}
        badge={mailbox
          ? <Badge tone="success" dot>{t('settingsPage.accountMailbox', 'Boîte du compte')}</Badge>
          : <Badge>{t('settingsPage.platformMailbox', 'Boîte de la plateforme')}</Badge>}
        description={t('settingsPage.carrierMailDesc', 'La boîte d’où partent, au nom du compte, les nouveaux e-mails aux transporteurs — leurs réponses y sont lues — et les signalements de bug. Connexion par mot de passe d’application, sans écran de consentement.')}
        flush
        footer={showForm && (
          <>
            {mailbox && smtpReplacing && (
              <button type="button" className="u-btn u-btn--secondary" onClick={() => setSmtpReplacing(false)} disabled={smtpSaving}>
                {t('common.cancel', 'Annuler')}
              </button>
            )}
            <button
              type="button"
              className="u-btn u-btn--primary"
              onClick={handleSaveSmtpConfig}
              disabled={smtpSaving || !smtpForm.email || !smtpForm.password}
            >
              {smtpSaving ? t('common.saving', 'Enregistrement…') : t('accountMailbox.connect', 'Connecter')}
            </button>
          </>
        )}
        footerNote={showForm && t('settingsPage.mailboxCheckNote', 'Unanimity vérifie l’envoi et la lecture avant d’enregistrer : rien n’est envoyé.')}
      >
        {smtpLoading ? (
          <div className="settings-v2__pad"><span className="u-skeleton" style={{ height: 56 }} /></div>
        ) : (
          <>
            {mailbox ? (
              <ul className="u-list">
                <li className="u-row">
                  <span className="u-logo" aria-hidden="true"><SiGmail color="#EA4335" /></span>
                  <div className="u-row__main">
                    <div className="u-row__title">{mailbox.email}</div>
                    <div className="u-row__meta u-row__meta--wrap">
                      {originPage
                        ? t('accountMailbox.fromOtp', 'Reprise de la boîte de double authentification de la fiche {{carrier}}.', { carrier: originPage.carrier_name })
                        : mailbox.origin?.kind === 'carrier_otp_mailbox'
                          ? t('accountMailbox.fromOtpGone', 'Reprise d’une boîte de double authentification.')
                          : t('accountMailbox.manual', 'Connectée avec son mot de passe d’application.')}
                      {' '}{t('accountMailbox.threadsNote', 'Les fils transporteur ouverts avant sa connexion restent sur la boîte de la plateforme.')}
                    </div>
                  </div>
                  <div className="u-row__side">
                    {!smtpReplacing && (
                      <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => setSmtpReplacing(true)} disabled={smtpSaving}>
                        {t('accountMailbox.replace', 'Changer de boîte')}
                      </button>
                    )}
                    <OverflowMenu
                      label={t('settingsPage.moreActions', 'Plus d’actions')}
                      items={[{
                        label: t('accountMailbox.disconnect', 'Déconnecter'),
                        icon: LogOut,
                        danger: true,
                        disabled: smtpSaving,
                        onClick: () => askConfirmation({
                          title: t('settingsPage.confirmMailboxTitle', 'Déconnecter {{email}} ?', { email: mailbox.email }),
                          message: t('accountMailbox.disconnectConfirm', 'Déconnecter la boîte Gmail du compte ? Les nouveaux e-mails transporteur partiront de la boîte de la plateforme, et les fils déjà ouverts depuis cette boîte ne pourront plus être poursuivis.'),
                          confirmLabel: t('accountMailbox.disconnect', 'Déconnecter'),
                          onConfirm: handleRemoveSmtpConfig,
                        }),
                      }]}
                    />
                  </div>
                </li>
              </ul>
            ) : (
              <div className="settings-v2__pad">
                <Banner tone="neutral">
                  {t('settingsPage.platformMailboxNote', 'Aujourd’hui, les e-mails aux transporteurs partent de la boîte partagée de la plateforme. Connectez la boîte Gmail du compte pour qu’ils partent à votre nom.')}
                </Banner>
              </div>
            )}
            {mailbox?.problem && (
              <div className="settings-v2__pad">
                <Banner tone="danger" title={t('settingsPage.attentionMailboxTitle', 'La boîte Gmail du compte ne fonctionne plus')}>
                  {mailbox.problem}
                </Banner>
              </div>
            )}
            {showForm && otherCandidates.length > 0 && (
              <ul className="u-list settings-v2__candidates">
                {otherCandidates.map((c) => (
                  <li className="u-row" key={c.carrier_id}>
                    <IconChip icon={KeyRound} size="sm" />
                    <div className="u-row__main">
                      <div className="u-row__title">{c.email}</div>
                      <div className="u-row__meta u-row__meta--wrap">
                        {t('accountMailbox.candidateHint', 'Boîte de double authentification de la fiche {{carrier}} : son adresse et son mot de passe d’application sont repris.', { carrier: c.carrier_name })}
                      </div>
                    </div>
                    <div className="u-row__side">
                      <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => handleUseOtpMailbox(c.carrier_id)} disabled={smtpSaving}>
                        {t('accountMailbox.useOtp', 'Utiliser cette boîte')}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {showForm && (
              <div className="settings-v2__pad">
                <div className="u-form-grid">
                  <Field label={t('settingsPage.gmailAddress', 'Adresse Gmail')} htmlFor="smtp-email">
                    <input
                      id="smtp-email"
                      type="email"
                      className="u-input"
                      value={smtpForm.email}
                      onChange={(e) => setSmtpForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="nom@entreprise.com"
                      disabled={smtpSaving}
                    />
                  </Field>
                  <Field
                    label={t('settingsPage.appPassword', 'Mot de passe d’application')}
                    htmlFor="smtp-password"
                    help={(
                      <>
                        {t('settingsPage.appPasswordHelp', 'Un mot de passe d’application Google, pas le mot de passe habituel.')}{' '}
                        <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer">
                          {t('settingsPage.appPasswordHelpLink', 'Créer un mot de passe d’application')}
                        </a>
                      </>
                    )}
                  >
                    <div className="u-input-group">
                      <input
                        id="smtp-password"
                        type={showSmtpPassword ? 'text' : 'password'}
                        className="u-input"
                        value={smtpForm.password}
                        onChange={(e) => setSmtpForm((f) => ({ ...f, password: e.target.value }))}
                        placeholder="abcd efgh ijkl mnop"
                        autoComplete="new-password"
                        disabled={smtpSaving}
                      />
                      <button
                        type="button"
                        className="u-input-group__btn"
                        onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                        aria-label={showSmtpPassword ? t('settingsPage.hide', 'Masquer') : t('settingsPage.show', 'Afficher')}
                        title={showSmtpPassword ? t('settingsPage.hide', 'Masquer') : t('settingsPage.show', 'Afficher')}
                      >
                        {showSmtpPassword ? <EyeOff /> : <Eye />}
                      </button>
                    </div>
                  </Field>
                  <Field label={t('settingsPage.smtpServer', 'Serveur d’envoi (SMTP)')} htmlFor="smtp-server">
                    <input
                      id="smtp-server"
                      type="text"
                      className="u-input"
                      value={smtpForm.smtp_server}
                      onChange={(e) => setSmtpForm((f) => ({ ...f, smtp_server: e.target.value }))}
                      placeholder="smtp.gmail.com"
                      disabled={smtpSaving}
                    />
                  </Field>
                  <Field label={t('settingsPage.smtpPort', 'Port')} htmlFor="smtp-port">
                    <input
                      id="smtp-port"
                      type="number"
                      className="u-input"
                      value={smtpForm.smtp_port}
                      onChange={(e) => setSmtpForm((f) => ({ ...f, smtp_port: e.target.value }))}
                      placeholder="587"
                      disabled={smtpSaving}
                    />
                  </Field>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    );
  };

  const renderSpacefill = () => {
    const formVisible = spacefillConfigured || spacefillFormOpen;
    const lastRun = spacefillSyncState?.last_run_at;
    return (
      <Card
        icon={Warehouse}
        title="Spacefill"
        badge={spacefillConfigured && (spacefillEnabled
          ? <Badge tone="success" dot>{t('settingsPage.connected', 'Connecté')}</Badge>
          : <Badge>{t('settingsPage.paused', 'En pause')}</Badge>)}
        description={t('settingsPage.spacefillDesc', 'Les incidents que vos clients ouvrent sur Spacefill arrivent comme tickets (avec leurs pièces jointes), et vous leur répondez depuis Unanimity. Synchronisation toutes les 5 minutes.')}
        actions={!formVisible && (
          <button type="button" className="u-btn u-btn--secondary" onClick={() => setSpacefillFormOpen(true)}>
            {t('settingsPage.configure', 'Configurer')}
          </button>
        )}
        footerNote={formVisible && spacefillConfigured && lastRun
          ? t('settingsPage.lastSync', 'Dernière synchronisation : {{date}}', { date: formatDateTime(lastRun) })
          : null}
        footer={formVisible && (
          <button
            type="button"
            className="u-btn u-btn--primary"
            onClick={handleSaveSpacefillConfig}
            disabled={isSavingSpacefill || isLoadingSpacefill}
          >
            {isSavingSpacefill ? t('common.saving', 'Enregistrement…') : t('settingsPage.save', 'Enregistrer')}
          </button>
        )}
      >
        {formVisible && (
          <div className="u-form">
            {spacefillConfigured && spacefillEnabled && spacefillSyncState?.last_error && (
              <Banner tone="warning" title={t('settingsPage.attentionSpacefillTitle', 'La synchronisation Spacefill échoue')}>
                {spacefillSyncState.last_error}
              </Banner>
            )}
            <div className="u-form-grid">
              <Field
                label={t('settingsPage.spacefillToken', 'Jeton d’API Spacefill')}
                htmlFor="spacefill-token"
                help={t('settingsPage.spacefillTokenHelp', 'Générez un jeton dans votre compte Spacefill (accès prestataire logistique), puis collez-le ici.')}
              >
                <div className="u-input-group">
                  <input
                    id="spacefill-token"
                    type={showSpacefillToken ? 'text' : 'password'}
                    className="u-input"
                    value={spacefillTokenInput}
                    onChange={(e) => setSpacefillTokenInput(e.target.value)}
                    placeholder={spacefillTokenSet
                      ? t('settingsPage.tokenSaved', 'Jeton enregistré — laissez vide pour le garder')
                      : t('settingsPage.spacefillTokenPlaceholder', 'Collez le jeton d’API')}
                    autoComplete="off"
                    disabled={isLoadingSpacefill || isSavingSpacefill}
                  />
                  <button
                    type="button"
                    className="u-input-group__btn"
                    onClick={() => setShowSpacefillToken(!showSpacefillToken)}
                    aria-label={showSpacefillToken ? t('settingsPage.hide', 'Masquer') : t('settingsPage.show', 'Afficher')}
                    title={showSpacefillToken ? t('settingsPage.hide', 'Masquer') : t('settingsPage.show', 'Afficher')}
                  >
                    {showSpacefillToken ? <EyeOff /> : <Eye />}
                  </button>
                </div>
              </Field>
              <Field
                label={t('settingsPage.spacefillTypes', 'Types d’incident importés')}
                htmlFor="spacefill-types"
                help={t('settingsPage.spacefillTypesHelp', 'Séparés par des virgules. Vide = tous les types ; un incident sans type est toujours importé.')}
              >
                <input
                  id="spacefill-types"
                  type="text"
                  className="u-input"
                  value={spacefillIncidentTypesInput}
                  onChange={(e) => setSpacefillIncidentTypesInput(e.target.value)}
                  placeholder={t('settingsPage.spacefillTypesPlaceholder', 'ex. Transport')}
                  disabled={isLoadingSpacefill || isSavingSpacefill}
                />
              </Field>
            </div>
            <Switch
              id="spacefill-enabled"
              checked={spacefillEnabled}
              onChange={setSpacefillEnabled}
              disabled={isLoadingSpacefill || isSavingSpacefill}
              label={t('settingsPage.spacefillEnabled', 'Synchroniser les incidents')}
            />
          </div>
        )}
      </Card>
    );
  };

  const renderTools = () => (
    <Card
      icon={Plug}
      title={t('settingsPage.toolsTitle', 'Outils et raccourcis')}
      description={t('settingsPage.toolsDesc', 'Les autres connexions du compte ont leur propre page.')}
      flush
    >
      <ul className="u-list">
        <li className="u-row">
          <IconChip icon={Warehouse} tone="neutral" />
          <div className="u-row__main">
            <div className="u-row__title">
              Unanimity WMS
              {user.unanimity_wms_id ? connectedBadge : notConnectedBadge}
            </div>
            <div className="u-row__meta">{t('settingsPage.wmsHint', 'Relie le compte à l’entrepôt Unanimity WMS.')}</div>
          </div>
          <div className="u-row__side">
            {!user.unanimity_wms_id && (
              <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={handleConnectWMS} disabled={isWmsConnecting}>
                {isWmsConnecting ? t('userParameter.connecting', 'Connexion…') : t('settingsPage.connect', 'Connecter')}
              </button>
            )}
          </div>
        </li>
        {[
          { key: 'connectors', icon: Database, title: t('settingsPage.dataSources', 'Sources de données'), meta: t('settingsPage.dataSourcesHint', 'WMS, ERP et API de suivi qui enrichissent les tickets.'), to: '/connectors' },
          { key: 'shops', icon: Store, title: t('settingsPage.shops', 'Boutiques'), meta: t('settingsPage.shopsHint', 'Shopify et PrestaShop : commandes, suivi, catalogue.'), to: '/shops' },
          { key: 'chatbot', icon: MessagesSquare, title: t('settingsPage.chatbot', 'Chatbot du site'), meta: t('settingsPage.chatbotHint', 'Le chatbot que vos clients voient sur votre site.'), to: '/chatbot-settings' },
        ].map((link) => (
          <li className="u-row" key={link.key}>
            <IconChip icon={link.icon} tone="neutral" />
            <div className="u-row__main">
              <div className="u-row__title">{link.title}</div>
              <div className="u-row__meta">{link.meta}</div>
            </div>
            <div className="u-row__side">
              <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => navigate(link.to)}>
                {t('settingsPage.open', 'Ouvrir')}
                <ChevronRight aria-hidden="true" />
              </button>
            </div>
          </li>
        ))}
      </ul>
      {user.shop_name && (
        <details className="u-disclosure settings-v2__advanced">
          <summary>
            <ChevronRight aria-hidden="true" />
            <SiShopify color="#5E8E3E" aria-hidden="true" />
            {t('settingsPage.shopifyLegacy', 'Avancé — webhooks de l’ancienne application Shopify')}
          </summary>
          <div className="settings-v2__webhooks">
            <p className="u-help">
              {t('settingsPage.shopifyLegacyHelp', 'Pour la boutique {{shop}} connectée par l’ancienne application. Les boutiques se connectent désormais depuis la page Boutiques.', { shop: user.shop_name })}
            </p>
            <div className="settings-v2__webhook-actions">
              <button
                type="button"
                className="u-btn u-btn--secondary u-btn--sm"
                onClick={handleRegisterOrderWebhook}
                disabled={isRegisteringWebhook || isRemovingWebhook || isRegisteringSaveOnlyWebhook}
              >
                {t('settingsPage.registerWebhook', 'Enregistrer le webhook de commandes')}
              </button>
              <button
                type="button"
                className="u-btn u-btn--secondary u-btn--sm"
                onClick={handleRegisterSaveOnlyWebhook}
                disabled={isRegisteringWebhook || isRemovingWebhook || isRegisteringSaveOnlyWebhook}
              >
                {t('settingsPage.registerSaveOnly', 'Webhook « enregistrement seul »')}
              </button>
              <button
                type="button"
                className="u-btn u-btn--danger u-btn--sm"
                onClick={() => askConfirmation({
                  title: t('settingsPage.confirmWebhooksTitle', 'Retirer les webhooks Shopify ?'),
                  message: t('settingsPage.confirmWebhooksMessage', 'Les nouvelles commandes de {{shop}} ne seront plus reçues par l’ancienne application.', { shop: user.shop_name }),
                  confirmLabel: t('settingsPage.removeWebhooks', 'Retirer les webhooks'),
                  onConfirm: handleRemoveOrderWebhook,
                })}
                disabled={isRegisteringWebhook || isRemovingWebhook || isRegisteringSaveOnlyWebhook}
              >
                {t('settingsPage.removeWebhooks', 'Retirer les webhooks')}
              </button>
            </div>
          </div>
        </details>
      )}
    </Card>
  );

  const renderIntegrations = () => (
    <div className="settings-v2__stack">
      <Card
        icon={Inbox}
        title={t('settingsPage.channelsTitle', 'Canaux de réception')}
        badge={<Badge tone={connectedChannels.length ? 'accent' : undefined}>{connectedChannels.length} / {CHANNEL_TOTAL}</Badge>}
        description={t('settingsPage.channelsDesc', 'Les messages de vos clients arrivent par ces canaux et deviennent des tickets.')}
        flush
      >
        <ul className="u-list">
          {channels.map((channel) => (
            <React.Fragment key={channel.key}>
              {renderChannelRow(channel)}
              {channel.key === 'outlook' && (
                <li className="settings-v2__nested">
                  {renderOutlookAccounts()}
                  {renderSubscriptions()}
                </li>
              )}
            </React.Fragment>
          ))}
        </ul>
      </Card>
      {renderCarrierMailbox()}
      {renderSpacefill()}
      {renderTools()}
    </div>
  );

  const renderNotifications = () => {
    const slackChanged = Object.keys(slackPayload()).length > 0;
    return (
      <div className="settings-v2__stack">
        <Card
          icon={BellRing}
          title={t('settingsPage.slackTitle', 'Alertes Slack')}
          badge={slackConfig && (slackConfig.configured
            ? <Badge tone="success" dot>{t('settingsPage.slackOn', 'Activées')}</Badge>
            : <Badge>{t('settingsPage.slackOff', 'Non configurées')}</Badge>)}
          description={t('settingsPage.slackDesc', 'Un message dans votre canal Slack à chaque nouveau ticket.')}
          footerNote={t('settingsPage.slackNote', 'Invitez le bot dans le canal pour qu’il puisse y écrire.')}
          footer={(
            <button
              type="button"
              className="u-btn u-btn--primary"
              onClick={handleSaveSlackCredentials}
              disabled={isSavingSlackCredentials || isLoadingSlackCredentials || !slackChanged}
            >
              {isSavingSlackCredentials ? t('common.saving', 'Enregistrement…') : t('settingsPage.save', 'Enregistrer')}
            </button>
          )}
        >
          <div className="u-form-grid">
            <Field label={t('settingsPage.slackToken', 'Jeton du bot')} htmlFor="slack-token" help={t('settingsPage.slackTokenHelp', 'Commence par xoxb-. Il n’est jamais réaffiché.')}>
              <div className="u-input-group">
                <input
                  id="slack-token"
                  type={showSlackToken ? 'text' : 'password'}
                  className="u-input"
                  value={slackToken}
                  onChange={(e) => setSlackToken(e.target.value)}
                  placeholder={slackConfig?.maskedToken
                    ? t('settingsPage.slackTokenSaved', 'Enregistré ({{masked}}) — laissez vide pour le garder', { masked: slackConfig.maskedToken })
                    : 'xoxb-…'}
                  autoComplete="off"
                  disabled={isLoadingSlackCredentials}
                />
                <button
                  type="button"
                  className="u-input-group__btn"
                  onClick={() => setShowSlackToken(!showSlackToken)}
                  aria-label={showSlackToken ? t('settingsPage.hide', 'Masquer') : t('settingsPage.show', 'Afficher')}
                  title={showSlackToken ? t('settingsPage.hide', 'Masquer') : t('settingsPage.show', 'Afficher')}
                >
                  {showSlackToken ? <EyeOff /> : <Eye />}
                </button>
              </div>
            </Field>
            <Field label={t('settingsPage.slackChannel', 'ID du canal')} htmlFor="slack-channel" help={t('settingsPage.slackChannelHelp', 'Dans Slack : détails du canal, tout en bas (ex. C01234567).')}>
              <input
                id="slack-channel"
                type="text"
                className="u-input"
                value={slackChannelId}
                onChange={(e) => setSlackChannelId(e.target.value)}
                placeholder="C01234567"
                disabled={isLoadingSlackCredentials}
              />
            </Field>
          </div>
        </Card>

        <Card
          icon={Ban}
          title={t('settingsPage.blockedTitle', 'Expéditeurs ignorés')}
          description={t('settingsPage.blockedDesc', 'Les e-mails de ces adresses ou de ces domaines n’ouvrent jamais de ticket.')}
          actions={blockedEntries.length > 6 && (
            <Segmented
              label={t('settingsPage.blockedFilter', 'Filtrer')}
              value={blockedFilter}
              onChange={setBlockedFilter}
              options={[
                { value: 'all', label: t('settingsPage.all', 'Tous'), count: blockedEntries.length },
                { value: 'email', label: t('settingsPage.addresses', 'Adresses'), count: blockedCounts.email },
                { value: 'domain', label: t('settingsPage.domains', 'Domaines'), count: blockedCounts.domain },
              ]}
            />
          )}
        >
          <div className="u-form">
            <div className="u-inline-form">
              <Field error={blockedEntryError}>
                <input
                  type="text"
                  className="u-input"
                  value={newBlockedEntry}
                  onChange={(e) => { setNewBlockedEntry(e.target.value); setBlockedEntryError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddBlockedEntry(); } }}
                  placeholder={t('settingsPage.blockedPlaceholder', 'nom@exemple.com ou exemple.com')}
                  aria-label={t('settingsPage.blockedAddLabel', 'Adresse ou domaine à ignorer')}
                  aria-invalid={!!blockedEntryError}
                  disabled={isLoadingBlockedEmails || isSavingBlockedEmails}
                />
              </Field>
              <button
                type="button"
                className="u-btn u-btn--primary"
                onClick={handleAddBlockedEntry}
                disabled={isSavingBlockedEmails || isLoadingBlockedEmails || !newBlockedEntry.trim()}
              >
                <Plus aria-hidden="true" />
                {t('settingsPage.add', 'Ajouter')}
              </button>
            </div>
            {blockedInvalid.length > 0 && (
              <Banner tone="warning" title={t('settingsPage.attentionBlockedTitle', '{{count}} expéditeur ignoré est illisible', { count: blockedInvalid.length })}>
                {t('settingsPage.blockedInvalidHelp', 'Corrigez-les ou retirez-les : {{entries}}', { entries: blockedInvalid.join(', ') })}
              </Banner>
            )}
            {blockedEntries.length === 0 && !isLoadingBlockedEmails ? (
              <EmptyState icon={Ban} title={t('settingsPage.blockedEmptyTitle', 'Aucun expéditeur ignoré')}>
                {t('settingsPage.blockedEmpty', 'Ajoutez une adresse ou un domaine (newsletters, notifications automatiques) pour qu’ils n’ouvrent plus de ticket.')}
              </EmptyState>
            ) : (
              <ul className="settings-v2__entries">
                {visibleBlocked.map((entry) => (
                  <li key={entry.value} className="settings-v2__entry">
                    <Badge tone={entry.kind === 'domain' ? 'info' : undefined}>
                      {entry.kind === 'domain' ? t('settingsPage.domain', 'Domaine') : t('settingsPage.address', 'Adresse')}
                    </Badge>
                    <span className="settings-v2__entry-value" title={entry.value}>{entry.value}</span>
                    <button
                      type="button"
                      className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
                      onClick={() => handleRemoveBlockedEntry(entry.value)}
                      disabled={isSavingBlockedEmails}
                      aria-label={t('settingsPage.removeEntry', 'Retirer {{value}}', { value: entry.value })}
                      title={t('settingsPage.removeEntry', 'Retirer {{value}}', { value: entry.value })}
                    >
                      <X aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>
    );
  };

  const renderSecurity = () => (
    <div className="settings-v2__stack">
      <Card
        icon={Lock}
        title={t('settingsPage.passwordTitle', 'Mot de passe')}
        description={t('settingsPage.passwordDesc', 'Le mot de passe de connexion à Unanimity.')}
        actions={(
          <button type="button" className="u-btn u-btn--secondary" onClick={() => setShowPasswordModal(true)}>
            {t('settingsPage.changePassword', 'Changer le mot de passe')}
          </button>
        )}
      />
      {user.fa_account && (
        <Card
          icon={KeyRound}
          title={t('settingsPage.otpTitle', 'Code OTP')}
          description={t('settingsPage.otpDesc', 'Le code de double authentification utilisé par le compte.')}
          actions={(
            <button type="button" className="u-btn u-btn--secondary" onClick={handleOpenOtpModal}>
              {t('settingsPage.changeOtp', 'Changer le code')}
            </button>
          )}
        />
      )}
      <Card
        tone="danger"
        icon={Trash2}
        iconTone="danger"
        title={t('settingsPage.deleteTitle', 'Supprimer le compte')}
        description={t('settingsPage.deleteDesc', 'Supprime définitivement le compte, ses tickets, ses canaux et ses réglages. Irréversible.')}
        actions={(
          <button type="button" className="u-btn u-btn--danger" onClick={() => { setDeleteError(''); setConfirmUsername(''); setShowDeleteModal(true); }}>
            {t('settingsPage.deleteAction', 'Supprimer le compte…')}
          </button>
        )}
      />
    </div>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="u-page u-page--narrow settings-v2">
      {/* Identity + views: the reference « header card » pattern. */}
      <section className="u-card settings-v2__hero">
        <div className="settings-v2__identity">
          <span className="u-avatar u-avatar--lg" aria-hidden="true">{initialsOf(displayName)}</span>
          <div className="settings-v2__identity-text">
            <span className="u-overline">{t('settingsPage.overline', 'Paramètres du compte')}</span>
            <h1 className="u-page-title">{displayName}</h1>
            <div className="settings-v2__meta">
              {user.email && <span>{user.email}</span>}
              {(roleLabels[user.role] || user.role) && <span>{roleLabels[user.role] || user.role}</span>}
              {user.subscription_plan && <Badge tone="accent">{user.subscription_plan}</Badge>}
            </div>
          </div>
        </div>
        <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} label={t('settingsPage.overline', 'Paramètres du compte')} />
      </section>

      {/* Everything below the header sits on the well (kit § Coque). */}
      <div className="u-well">
      {/* State of the account at a glance — each tile leads to its tab. */}
      <div className="u-kpi-row">
        {user.subscription_plan && (
          <Kpi
            icon={CreditCard}
            label={t('settingsPage.kpiPlan', 'Abonnement')}
            value={user.subscription_plan}
            context={planEnd
              ? t('settingsPage.kpiPlanUntil', 'jusqu’au {{date}}', { date: formatDate(user.subscription_end_date) })
              : intervalLabels[String(user.billing_interval).toLowerCase()] || t('settingsPage.kpiPlanCurrent', 'formule en cours')}
            active={activeTab === TABS.PROFILE}
            onClick={() => setActiveTab(TABS.PROFILE)}
          />
        )}
        <Kpi
          icon={Inbox}
          tone={connectedChannels.length ? undefined : 'neutral'}
          label={t('settingsPage.kpiChannels', 'Canaux connectés')}
          value={connectedChannels.length}
          unit={`/ ${CHANNEL_TOTAL}`}
          context={connectedChannels.length
            ? connectedChannels.map((channel) => channel.name).join(' · ')
            : t('settingsPage.kpiNoChannel', 'Aucun canal de messagerie')}
          meter={{ value: connectedChannels.length, max: CHANNEL_TOTAL, label: t('settingsPage.kpiChannels', 'Canaux connectés') }}
          active={activeTab === TABS.INTEGRATIONS}
          onClick={() => setActiveTab(TABS.INTEGRATIONS)}
        />
        {smtpConfig && (
          <Kpi
            icon={Send}
            tone={mailbox?.problem ? 'danger' : mailbox ? 'success' : 'neutral'}
            label={t('settingsPage.kpiCarrierMail', 'E-mails transporteurs')}
            value={mailbox ? t('settingsPage.kpiOwnMailbox', 'Votre boîte') : t('settingsPage.platformShort', 'Plateforme')}
            context={mailbox?.problem
              ? t('settingsPage.kpiMailboxProblem', 'La boîte ne répond plus')
              : mailbox ? mailbox.email : t('settingsPage.kpiPlatformContext', 'boîte partagée Unanimity')}
            contextTone={mailbox?.problem ? 'danger' : undefined}
            active={activeTab === TABS.INTEGRATIONS}
            onClick={() => setActiveTab(TABS.INTEGRATIONS)}
          />
        )}
        {slackConfig && (
          <Kpi
            icon={BellRing}
            tone={slackConfig.configured ? undefined : 'neutral'}
            label={t('settingsPage.kpiSlack', 'Alertes Slack')}
            value={slackConfig.configured ? t('settingsPage.slackOn', 'Activées') : t('settingsPage.kpiOff', 'Désactivées')}
            context={slackConfig.configured
              ? t('settingsPage.kpiSlackChannel', 'canal {{channel}}', { channel: slackConfig.channelId })
              : t('settingsPage.kpiSlackHint', 'un message par nouveau ticket')}
            active={activeTab === TABS.NOTIFICATIONS}
            onClick={() => setActiveTab(TABS.NOTIFICATIONS)}
          />
        )}
        {!isLoadingBlockedEmails && (
          <Kpi
            icon={Ban}
            tone="neutral"
            label={t('settingsPage.kpiBlocked', 'Expéditeurs ignorés')}
            value={blockedEntries.length}
            context={blockedInvalid.length
              ? t('settingsPage.kpiBlockedInvalid', '{{count}} illisible à corriger', { count: blockedInvalid.length })
              : t('settingsPage.kpiBlockedSplit', 'adresses : {{emails}} · domaines : {{domains}}', { emails: blockedCounts.email, domains: blockedCounts.domain })}
            contextTone={blockedInvalid.length ? 'warning' : undefined}
            active={activeTab === TABS.NOTIFICATIONS}
            onClick={() => setActiveTab(TABS.NOTIFICATIONS)}
          />
        )}
      </div>

      {attention.length > 0 && (
        <Card
          icon={TriangleAlert}
          iconTone="warning"
          title={t('settingsPage.attentionTitle', 'À vérifier')}
          badge={<Badge tone="warning">{attention.length}</Badge>}
          flush
          className="settings-v2__attention"
        >
          <ul className="u-list">
            {attention.map((item) => (
              <li className="u-row" key={item.key}>
                <Badge tone={item.tone} dot>
                  {item.tone === 'danger' ? t('settingsPage.critical', 'Bloquant') : t('settingsPage.watch', 'À surveiller')}
                </Badge>
                <div className="u-row__main">
                  <div className="u-row__title">{item.title}</div>
                  <div className="u-row__meta" title={item.detail}>{item.detail}</div>
                </div>
                <div className="u-row__side">
                  <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => setActiveTab(item.tab)}>
                    {t('settingsPage.see', 'Voir')}
                    <ChevronRight aria-hidden="true" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div role="tabpanel">
        {activeTab === TABS.PROFILE && renderProfile()}
        {activeTab === TABS.INTEGRATIONS && renderIntegrations()}
        {activeTab === TABS.NOTIFICATIONS && renderNotifications()}
        {activeTab === TABS.SECURITY && renderSecurity()}
      </div>
      </div>

      {feedback && (
        <Toast tone={feedback.type === 'error' ? 'danger' : 'success'} onClose={() => setFeedback(null)} closeLabel={t('common.close', 'Fermer')}>
          {feedback.text}
        </Toast>
      )}

      {confirmation && (
        <ConfirmDialog
          title={confirmation.title}
          message={confirmation.message}
          confirmLabel={confirmation.confirmLabel}
          cancelLabel={t('common.cancel', 'Annuler')}
          onConfirm={runConfirmation}
          onCancel={() => setConfirmation(null)}
          busy={isConfirming}
        />
      )}

      {showPasswordModal && (
        <Modal
          title={t('settingsPage.changePassword', 'Changer le mot de passe')}
          icon={Lock}
          onClose={closePasswordModal}
          closeLabel={t('common.close', 'Fermer')}
          footer={(
            <>
              <button type="button" className="u-btn u-btn--secondary" onClick={closePasswordModal}>
                {t('common.cancel', 'Annuler')}
              </button>
              <button type="submit" form="settings-password-form" className="u-btn u-btn--primary">
                {t('settingsPage.updatePassword', 'Mettre à jour')}
              </button>
            </>
          )}
        >
          <form id="settings-password-form" className="u-form" onSubmit={handlePasswordSubmit}>
            {passwordError && <Banner tone="danger">{passwordError}</Banner>}
            <Field label={t('settingsPage.currentPassword', 'Mot de passe actuel')} htmlFor="pw-current">
              <input id="pw-current" type="password" className="u-input" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} autoComplete="current-password" required />
            </Field>
            <Field label={t('settingsPage.newPassword', 'Nouveau mot de passe')} htmlFor="pw-new">
              <input id="pw-new" type="password" className="u-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" required />
            </Field>
            <Field label={t('settingsPage.confirmPassword', 'Confirmer le nouveau mot de passe')} htmlFor="pw-confirm">
              <input id="pw-confirm" type="password" className="u-input" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} autoComplete="new-password" required />
            </Field>
          </form>
        </Modal>
      )}

      {showDeleteModal && (
        <Modal
          title={t('settingsPage.deleteTitle', 'Supprimer le compte')}
          icon={Trash2}
          iconTone="danger"
          onClose={() => setShowDeleteModal(false)}
          closeLabel={t('common.close', 'Fermer')}
          footer={(
            <>
              <button type="button" className="u-btn u-btn--secondary" onClick={() => setShowDeleteModal(false)}>
                {t('common.cancel', 'Annuler')}
              </button>
              <button type="submit" form="settings-delete-form" className="u-btn u-btn--danger-solid" disabled={confirmUsername !== user.username}>
                {t('settingsPage.deleteForever', 'Supprimer définitivement')}
              </button>
            </>
          )}
        >
          <form id="settings-delete-form" className="u-form" onSubmit={handleDeleteUser}>
            <Banner tone="danger">
              {t('settingsPage.deleteWarning', 'Cette action est irréversible : toutes les données du compte sont supprimées.')}
            </Banner>
            <Field
              label={t('settingsPage.deleteTypeLabel', 'Saisissez « {{username}} » pour confirmer', { username: user.username })}
              htmlFor="delete-confirm"
              error={deleteError}
            >
              <input id="delete-confirm" type="text" className="u-input" value={confirmUsername} onChange={(e) => { setConfirmUsername(e.target.value); setDeleteError(''); }} autoComplete="off" required />
            </Field>
          </form>
        </Modal>
      )}

      {showWhatsAppModal && (
        <Modal
          title={t('settingsPage.whatsappModalTitle', 'Connecter WhatsApp Business')}
          icon={MessagesSquare}
          onClose={() => setShowWhatsAppModal(false)}
          closeLabel={t('common.close', 'Fermer')}
          wide
        >
          <WhatsAppIntegration />
        </Modal>
      )}

      {showOtpModal && (
        <Modal
          title={t('settingsPage.otpModalTitle', 'Changer le code OTP')}
          icon={KeyRound}
          onClose={handleCloseOtpModal}
          closeLabel={t('common.close', 'Fermer')}
          footer={(
            <>
              <button type="button" className="u-btn u-btn--secondary" onClick={handleCloseOtpModal}>
                {t('common.cancel', 'Annuler')}
              </button>
              <button type="submit" form="settings-otp-form" className="u-btn u-btn--primary" disabled={isSubmittingOtp}>
                {isSubmittingOtp ? t('userParameter.updatingOtp', 'Updating...') : t('settingsPage.updateOtp', 'Mettre à jour')}
              </button>
            </>
          )}
        >
          <form id="settings-otp-form" className="u-form" onSubmit={handleSubmitOtpChange}>
            <Field
              label={t('userParameter.newOtpCode', 'New OTP Code')}
              htmlFor="otp-code"
              help={t('userParameter.otpCodeHelp', 'Enter the new OTP code from your authenticator app')}
            >
              <input
                id="otp-code"
                type="text"
                className="u-input"
                value={newOtpCode}
                onChange={(e) => setNewOtpCode(e.target.value)}
                placeholder={t('userParameter.enterNewOtpCode', 'Enter your new OTP code')}
                required
              />
            </Field>
            {submitOtpError && <Banner tone="danger">{submitOtpError}</Banner>}
            {submitOtpSuccess && <Banner tone="success">{submitOtpSuccess}</Banner>}
          </form>
        </Modal>
      )}
    </div>
  );
};

// A team member (role employee) gets « Mon compte » only: the password
// (Kevin, 29/10). Everyone else gets the account settings above, unchanged.
const UserParameter = () => (isEmployeeSession() ? <UserParameterEmployee /> : <AccountParameters />);

export default UserParameter;
